import React from 'react';
import { X } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

interface Announcement {
  id: number;
  datetime: string | number; // YYYYMMDDHHmm形式（文字列または数値）
  title: string;
  content: string;
  priority: 'normal' | 'high' | 'urgent';
}

interface AnnouncementModalProps {
  announcements: Announcement[];
  onClose: (latestId: number) => void;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ 
  announcements, 
  onClose 
}) => {
  const { t } = useI18n();
  if (announcements.length === 0) {
    return null;
  }

  const handleClose = () => {
    // 最大のIDを取得
    const latestId = Math.max(...announcements.map(a => a.id));
    onClose(latestId);
  };

  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-red-500 bg-red-50';
      case 'high':
        return 'border-yellow-500 bg-yellow-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">{t('announcement.urgent')}</span>;
      case 'high':
        return <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">{t('announcement.important')}</span>;
      default:
        return null;
    }
  };

  const formatDateTime = (datetime: string | number) => {
    // 数値の場合は文字列に変換
    const datetimeStr = String(datetime);
    
    // YYYYMMDDHHmm形式を解析
    const year = parseInt(datetimeStr.slice(0, 4));
    const month = parseInt(datetimeStr.slice(4, 6));
    const day = parseInt(datetimeStr.slice(6, 8));
    const hour = parseInt(datetimeStr.slice(8, 10));
    const minute = parseInt(datetimeStr.slice(10, 12));
    
    const date = new Date(year, month - 1, day, hour, minute);
    
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content: string) => {
    // HTMLタグが含まれていない場合は改行を<br>に変換
    if (!content.includes('<')) {
      return content.replace(/\n/g, '<br>');
    }
    // HTMLタグが含まれている場合はそのまま返す
    return content;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {t('announcement.title')} {announcements.length > 1 && t('announcement.count', { count: announcements.length })}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              {announcements.map((announcement, index) => (
                <div
                  key={announcement.id}
                  className={`p-4 border-2 rounded-lg transition-all ${getPriorityStyles(announcement.priority)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg text-gray-900 flex-1">
                      {announcement.title}
                    </h3>
                    {getPriorityBadge(announcement.priority)}
                  </div>
                  
                  <div className="text-sm text-gray-500 mb-3">
                    {formatDateTime(announcement.datetime)}
                  </div>
                  
                  <div 
                    className="announcement-content prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: formatContent(announcement.content) }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t px-6 py-4">
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('announcement.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;